export const CONTACT_EMAIL = "contact@aiwatermark.studio";
export const LAST_UPDATED = { en: "June 2026", fr: "juin 2026" };

export const legal = {
  en: {
    title: "Legal notice",
    intro:
      "AIwatermark.studio is a free web tool that runs entirely inside your browser. This page identifies the publisher of the site and the terms under which it is made available.",
    sections: [
      {
        h: "Publisher",
        p: [
          "Site: AIwatermark.studio — https://aiwatermark.studio",
          `Contact: ${CONTACT_EMAIL}`,
          "The site is published and operated by an independent editor. It is a non-commercial project: there is no account, no subscription and no payment of any kind.",
        ],
      },
      {
        h: "Hosting",
        p: [
          "The static site is hosted on a cloud infrastructure operated by Emergent (emergent.sh). Only the pages, scripts and styles of the site are served from that infrastructure.",
        ],
      },
      {
        h: "Purpose of the service",
        p: [
          "AIwatermark.studio detects and removes provenance markers from files and text you already own: invisible Unicode carriers, C2PA / Content Credentials manifests, EXIF and XMP metadata, document properties, MP4/MOV metadata boxes, statistical text watermarks and SynthID-class pixel watermarks.",
          "All processing is performed locally by your browser. Your files are never transmitted to a server.",
        ],
      },
      {
        h: "Acceptable use",
        p: [
          "The tool is provided for content you own or are authorised to modify. You are solely responsible for the use you make of it and for complying with the laws that apply to you, including copyright, platform terms of service and any obligation to disclose AI-generated content.",
          "Using the tool to misappropriate someone else's work, to remove a rights holder's identification, or to circumvent a legal transparency obligation is not permitted.",
        ],
      },
      {
        h: "Best-effort results, no warranty",
        p: [
          "Metadata and Unicode removals are verifiable and reported as such. Statistical text disruption and pixel-watermark disruption are best-effort by nature: no in-browser tool can guarantee that a proprietary detector will fail.",
          "The service is provided \"as is\", without warranty of any kind. The publisher cannot be held liable for any loss of data, alteration of a file, or consequence arising from the use of the results.",
        ],
      },
      {
        h: "Intellectual property",
        p: [
          "The name, interface and source code of the site belong to their respective authors. The files and texts you process remain entirely yours — the site acquires no right over them, since it never receives them.",
        ],
      },
      {
        h: "Applicable law",
        p: [
          "This notice is governed by French law. Any question can be sent to " + CONTACT_EMAIL + ".",
        ],
      },
    ],
  },
  fr: {
    title: "Mentions légales",
    intro:
      "AIwatermark.studio est un outil web gratuit qui fonctionne entièrement dans votre navigateur. Cette page identifie l'éditeur du site et les conditions dans lesquelles il est mis à disposition.",
    sections: [
      {
        h: "Éditeur",
        p: [
          "Site : AIwatermark.studio — https://aiwatermark.studio",
          `Contact : ${CONTACT_EMAIL}`,
          "Le site est édité et exploité par un éditeur indépendant. Il s'agit d'un projet non commercial : aucun compte, aucun abonnement, aucun paiement.",
        ],
      },
      {
        h: "Hébergement",
        p: [
          "Le site statique est hébergé sur une infrastructure cloud exploitée par Emergent (emergent.sh). Seuls les pages, scripts et styles du site sont servis depuis cette infrastructure.",
        ],
      },
      {
        h: "Objet du service",
        p: [
          "AIwatermark.studio détecte et efface les marques de provenance de fichiers et de textes qui vous appartiennent : caractères Unicode invisibles, manifestes C2PA / Content Credentials, métadonnées EXIF et XMP, propriétés de documents, boîtes de métadonnées MP4/MOV, filigranes statistiques du texte et filigranes de pixels de type SynthID.",
          "Tous les traitements sont effectués localement par votre navigateur. Vos fichiers ne sont jamais transmis à un serveur.",
        ],
      },
      {
        h: "Usage autorisé",
        p: [
          "L'outil est fourni pour des contenus dont vous êtes titulaire ou que vous êtes autorisé à modifier. Vous êtes seul responsable de l'usage que vous en faites et du respect des lois qui vous sont applicables, notamment le droit d'auteur, les conditions d'utilisation des plateformes et toute obligation de signaler un contenu généré par IA.",
          "Utiliser l'outil pour s'approprier l'œuvre d'un tiers, retirer l'identification d'un ayant droit ou contourner une obligation légale de transparence n'est pas autorisé.",
        ],
      },
      {
        h: "Résultats en meilleur effort, sans garantie",
        p: [
          "La suppression des métadonnées et des caractères Unicode est vérifiable et rapportée comme telle. La perturbation statistique du texte et celle des filigranes de pixels sont par nature en meilleur effort : aucun outil dans le navigateur ne peut garantir l'échec d'un détecteur propriétaire.",
          "Le service est fourni « en l'état », sans garantie d'aucune sorte. L'éditeur ne peut être tenu responsable d'une perte de données, de l'altération d'un fichier, ni d'aucune conséquence liée à l'utilisation des résultats.",
        ],
      },
      {
        h: "Propriété intellectuelle",
        p: [
          "Le nom, l'interface et le code source du site appartiennent à leurs auteurs respectifs. Les fichiers et textes que vous traitez restent entièrement les vôtres — le site n'acquiert aucun droit dessus, puisqu'il ne les reçoit jamais.",
        ],
      },
      {
        h: "Droit applicable",
        p: [
          "Les présentes mentions sont régies par le droit français. Toute question peut être adressée à " + CONTACT_EMAIL + ".",
        ],
      },
    ],
  },
};

export const privacy = {
  en: {
    title: "Privacy policy",
    intro:
      "Short version: your files and your texts never leave your browser. There is no account, no upload and no storage of your content on our side.",
    sections: [
      {
        h: "What we never collect",
        p: [
          "Your files. Images, PDFs, DOCX, videos and any other document are opened, parsed and rewritten by JavaScript running in your own tab. There is no upload endpoint.",
          "Your texts. What you paste into the Text tool stays in the memory of the page and is discarded when you close or reload it.",
          "Your file names, your metadata, your detection reports. None of this is transmitted.",
        ],
      },
      {
        h: "What is stored on your device",
        p: [
          "A single preference is saved in your browser's local storage: the interface language (key wm-lang). It never leaves your device and you can delete it by clearing your site data.",
          "The site does not use advertising cookies and does not sell any data.",
        ],
      },
      {
        h: "Anonymous usage measurement",
        p: [
          "An anonymous counter of cleaned items is incremented so the site can display a total. It carries no identifier, no content and no file name.",
          "Audience measurement (PostHog, hosted by Emergent) records page views and anonymised interaction events to understand how the site is used. It never captures the content of your files or of your texts.",
        ],
      },
      {
        h: "Server logs",
        p: [
          "Like any website, the host records technical connection logs (IP address, date, requested page, user agent) for security and availability purposes. These logs are kept for a limited period and are not used for profiling.",
        ],
      },
      {
        h: "Your rights",
        p: [
          "Because we hold no personal file and no account, there is nothing to export or delete on our side. For any question about the technical logs or the audience measurement, write to " + CONTACT_EMAIL + ".",
        ],
      },
      {
        h: "Children",
        p: [
          "The service is not aimed at children under 15 and collects no data allowing an age to be determined.",
        ],
      },
      {
        h: "Changes",
        p: [
          "This policy may be updated as the tool evolves. The date at the top of the page always reflects the latest version.",
        ],
      },
    ],
  },
  fr: {
    title: "Politique de confidentialité",
    intro:
      "Version courte : vos fichiers et vos textes ne quittent jamais votre navigateur. Aucun compte, aucun envoi, aucun stockage de vos contenus de notre côté.",
    sections: [
      {
        h: "Ce que nous ne collectons jamais",
        p: [
          "Vos fichiers. Images, PDF, DOCX, vidéos et tout autre document sont ouverts, analysés et réécrits par du JavaScript exécuté dans votre propre onglet. Il n'existe aucun point d'envoi.",
          "Vos textes. Ce que vous collez dans l'outil Texte reste dans la mémoire de la page et disparaît dès que vous la fermez ou la rechargez.",
          "Vos noms de fichiers, vos métadonnées, vos rapports de détection. Rien de tout cela n'est transmis.",
        ],
      },
      {
        h: "Ce qui est stocké sur votre appareil",
        p: [
          "Une seule préférence est enregistrée dans le stockage local de votre navigateur : la langue de l'interface (clé wm-lang). Elle ne quitte jamais votre appareil et vous pouvez la supprimer en effaçant les données du site.",
          "Le site n'utilise aucun cookie publicitaire et ne vend aucune donnée.",
        ],
      },
      {
        h: "Mesure d'usage anonyme",
        p: [
          "Un compteur anonyme d'éléments nettoyés est incrémenté afin d'afficher un total sur le site. Il ne contient aucun identifiant, aucun contenu et aucun nom de fichier.",
          "La mesure d'audience (PostHog, hébergé par Emergent) enregistre les pages vues et des événements d'interaction anonymisés pour comprendre l'usage du site. Elle ne capte jamais le contenu de vos fichiers ni de vos textes.",
        ],
      },
      {
        h: "Journaux serveur",
        p: [
          "Comme tout site web, l'hébergeur conserve des journaux techniques de connexion (adresse IP, date, page demandée, agent utilisateur) à des fins de sécurité et de disponibilité. Ces journaux sont conservés pour une durée limitée et ne servent pas à du profilage.",
        ],
      },
      {
        h: "Vos droits",
        p: [
          "Comme nous ne détenons aucun fichier personnel ni aucun compte, il n'y a rien à exporter ou à supprimer de notre côté. Pour toute question sur les journaux techniques ou la mesure d'audience, écrivez à " + CONTACT_EMAIL + ".",
        ],
      },
      {
        h: "Mineurs",
        p: [
          "Le service ne s'adresse pas aux enfants de moins de 15 ans et ne collecte aucune donnée permettant de déterminer un âge.",
        ],
      },
      {
        h: "Évolutions",
        p: [
          "Cette politique peut être mise à jour au fil de l'évolution de l'outil. La date en haut de page indique toujours la dernière version.",
        ],
      },
    ],
  },
};
